import {AfterViewInit, Component, ElementRef, OnInit, ViewChild} from "@angular/core";
import {DecimalPipe} from "@angular/common";
import {WarmwassercheckAnfrage} from "../anfrage/warmwassercheck.anfrage";
import {DiaglogData} from "../../interfaces/diaglog.data";
import {UntypedFormGroup} from "@angular/forms";
import {WarmwassercheckRequestService} from "../services/request.service";
import {PortalService} from "../../services/portal.service";
import {WarmwassercheckConstants} from "../warmwassercheck.constants";
import {ElementService} from "../../services/element.service";


@Component({
    selector: 'app-ergebnis-geraeteauswertung',
    templateUrl: './ergebnis.component.html',
    providers: [DecimalPipe],
    standalone: false
})

export class ErgebnisComponent implements OnInit, AfterViewInit {
  /**
   * the current userdata
   */
  anfrage: WarmwassercheckAnfrage;

  /**
   * indicator if a result is avaibalbe
   */
  hasResult: boolean = false;

  /**
   * The analyseWrapper element
   */
  @ViewChild('analyseWrapper') analyseWrapper: ElementRef;

  /**
   * textkey prefix
   */
  textkeyPrefix: string = "warmwassercheck.ergebnis";

  /**
   * toggle if this is an internal project or not.
   */
  internalPortal: boolean;

  /**
   * Placeholder used in all texts
   */
  textPlaceholder: any = {};

  /**
   * url of the report for the download and newsletter
   */
  reportUrl: string;

  /**
   * toggle to force the (newsletter) dialog
   */
  forceDialog: boolean = false;

  /**
   * data that is sent to the backend when the dialog form is submitted
   */
  dialogData: DiaglogData;

  /**
   * toggle to show/hide the download element
   */
  showDownload: boolean = false;

  /**
   * form for validation
   */
  form: UntypedFormGroup;

  /**
   * holds the previous link
   */
  previousLink: string = '../../eingabe/';

  /**
   * holds the recommandation topics for the accordion element
   */
  replacementPlaceholder: any;

  /**
   * holds the url to the hec
   */
  hecUrl: string = null;

  /**
   * holds the id of the html element where to jump to after click on dialog hint
   */
  idTargetElement: string = null;

  /**
   * get the warmwasser constants
   */
  protected readonly WarmwassercheckConstants = WarmwassercheckConstants;

  /**
   * constructor
   * @param requestService
   * @param portalService
   * @param elementService
   * @param element
   * @param decimalPipe
   */
  constructor(protected requestService: WarmwassercheckRequestService, protected portalService: PortalService, protected elementService: ElementService, protected element: ElementRef, protected decimalPipe: DecimalPipe) {}

  /**
   * @inheritDoc
   */
  ngOnInit() {
    // get the anfrage data
    this.anfrage = this.requestService.getAnfrage();

    // check if we have a result
    this.hasResult = this.anfrage?.result?.hasOwnProperty('result');

    // init placeholder
    this.initPlaceholder();

    // check if it is an internal portal
    this.internalPortal = this.portalService.isInternalPortal();

    // set report url
    this.reportUrl = this.anfrage?.result?.report ?? null;

    // show/hide download module
    const hideReportDownload = this.portalService.getConfigAsBool('embed.newsletter.report.download');
    this.showDownload = !hideReportDownload && this.reportUrl && !this.internalPortal;

    // should the dialog be visible
    this.forceDialog = this.portalService.getConfigAsBool('embed.newsletter.force');

    // setup dialog data
    this.initDialogData();

    // inits the target element in dialog hint
    this.initTargetElement();

    // define some variables
    const isMfh = this.anfrage?.userInput?.gebaeudetyp === WarmwassercheckConstants.GEBAEUDETYP_MFH;
    const isGebaeudezentral = this.anfrage?.userInput?.warmwasser_bereitungstyp === WarmwassercheckConstants.WARMWASSER_BEREITUNGSTYP_GEBAEUDEZENTRAL;
    const isVerbrauchBekannt = this.anfrage?.userInput?.warmwasser_verbrauch_bekannt === 'ja';

    // set the correct previous link for the back button
    if (isMfh && isGebaeudezentral) {
      this.previousLink += isVerbrauchBekannt ?
        WarmwassercheckConstants.LAST_STEP_OF_CONSUMPTION_BEKANNT :
        WarmwassercheckConstants.LAST_STEP_OF_CONSUMPTION_GESCHAETZT;
    }
    else if (!isGebaeudezentral) {
      this.previousLink +=  WarmwassercheckConstants.STEP_GERAETEDATEN_2;
    }
    else {
      this.previousLink += WarmwassercheckConstants.LAST_STEP_OF_GERAETEDATEN;
    }

    // get the url for hec
    this.hecUrl = this.portalService.getConfig("alladvisors.link.heizcheck")?.replace(/\{\{portalId}}/, this.portalService.currentPortalId)?? null;
  }

  /**
   * @inheritDoc
   */
  ngAfterViewInit() {
    // init tooltips
    this.elementService.initTooltips(this.element);
  }

  /**
   * create data object that will be sent to the backend with the registration for the dialog
   */
  initDialogData(): void {
    this.requestService.backendService.getRequestId().subscribe(requestId => {
      this.dialogData = {
        requestId,
        bmOptinSource: window.location.href,
        doisource: 'wwc'
      };
    });
  }

  /**
   * Function is called if the users registered for the dialog
   */
  dialogRegistered(result: string): void {
    const anfrage = this.requestService.getAnfrage();
    anfrage.dialogResult = result;

    this.requestService.setAnfrage(anfrage, true);
  }


  /**
   * init the target element in dialog hint
   */
  initTargetElement(): void {
    // add css id to dialog or download module
    if (this.forceDialog) {
      this.idTargetElement = 'lead';
    }
    else if (this.showDownload) {
      this.idTargetElement = 'download';
    }
  }

  /**
   * init placeholder
   */
  initPlaceholder(): void {
    // get the placeholder values for the texts
    if(this.anfrage?.result?.placeholder) {
      // set the text placeholder
      this.textPlaceholder = this.anfrage?.result?.placeholder;
    }
    else {
      this.textPlaceholder = {};
    }

    // set device replacement placeholder
    const replacementPlaceholder = this.anfrage?.result?.placeholder?.device_replacement ?? null;

    if(replacementPlaceholder !== null) {
      const co2_savings = replacementPlaceholder?.co2_savings < 1 ? replacementPlaceholder?.co2_savings * 1000 : replacementPlaceholder?.co2_savings;

      this.replacementPlaceholder =
        [
          {name: 'euro_kosten', amount: this.decimalPipe.transform(replacementPlaceholder.installation_cost, '1.0-0'), icon: 'money-coins', type: replacementPlaceholder.recommendation_type},
          {name: 'jahre_amortisation', amount: replacementPlaceholder.amortization, icon: 'scale', type: replacementPlaceholder.recommendation_type},
          {name: 'euro_einsparung', amount: this.decimalPipe.transform(replacementPlaceholder.savings_15_years, '1.0-0'), icon: 'tipps-costsave-effectively', type: replacementPlaceholder.recommendation_type},
          {name: 'co2_einsparung', amount: this.decimalPipe.transform(co2_savings, '1.0-1'), icon: 'save-co2', type: replacementPlaceholder.recommendation_type, unit: replacementPlaceholder.co2_savings_plain < 1 ? 'kg' : 't'}
        ];
    }
  }
}
